import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Car,
  ChevronRight,
  Download,
  FileUser,
  Filter,
  IdCard,
  Languages,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  Sparkles,
  Trash2,
  UserSearch,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames, formatDate } from '@/lib/utils';
import EmptyState from '@/components/ui/EmptyState';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import Estrellas from '@/components/candidatos/Estrellas';
import { ChipsCatalogo } from '@/components/candidatos/Chips';
import FormularioCandidato from '@/components/candidatos/FormularioCandidato';
import NuevoDesdeCv from '@/components/candidatos/NuevoDesdeCv';
import { useCatalogos } from '@/components/candidatos/catalogos';
import {
  DEDICACION_LABELS,
  ESTADO_BADGES,
  ESTADO_LABELS,
  FUENTE_LABELS,
  IDIOMAS_LABELS,
  cvPrincipal,
  experienciaBreve,
  nombreCompleto,
  type Candidato,
  type ContadoresCandidatos,
  type EstadoCandidato,
} from '@/lib/candidatos';

/**
 * La bolsa de empleo.
 *
 * La pregunta que esta pantalla tiene que contestar en diez segundos es
 * siempre la misma: «¿tenemos a alguien de limpieza con coche que pueda
 * trabajar en Zahara en junio?». De ahí que los filtros sean puesto, zona,
 * vehículo y disponibilidad, y no un buscador a secas.
 *
 * Tarjetas en el móvil y tabla a partir de `md`, como el resto del CRM: los CV
 * llegan por WhatsApp y se revisan desde el teléfono.
 */

type Pestana = 'all' | EstadoCandidato | 'caducados';

const PESTANAS: { key: Pestana; label: string }[] = [
  { key: 'nuevo', label: 'Nuevos' },
  { key: 'en_bolsa', label: 'En bolsa' },
  { key: 'preseleccionado', label: 'Preseleccionados' },
  { key: 'entrevista', label: 'Entrevista' },
  { key: 'contratado', label: 'Contratados' },
  { key: 'descartado', label: 'Descartados' },
  { key: 'caducados', label: 'Caducados' },
  { key: 'all', label: 'Todos' },
];

function EstadoBadge({ estado }: { estado: EstadoCandidato }) {
  return (
    <span className={classNames('badge', ESTADO_BADGES[estado] || 'bg-gray-100 text-gray-500')}>
      {ESTADO_LABELS[estado] || estado}
    </span>
  );
}

/** Los idiomas que de verdad se pueden usar con un huésped. */
function idiomasUtiles(c: Candidato): string[] {
  return (c.idiomas || [])
    .filter((i) => i.nivel === 'alto' || i.nivel === 'nativo')
    .map((i) => IDIOMAS_LABELS[i.codigo] || i.codigo);
}

export default function CandidatosPage() {
  const [params, setParams] = useSearchParams();
  const navegar = useNavigate();
  const { catalogos } = useCatalogos();

  const pestana = (params.get('estado') as Pestana) || 'nuevo';
  const [busqueda, setBusqueda] = useState('');
  const [enCv, setEnCv] = useState(false);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [puesto, setPuesto] = useState('');
  const [zona, setZona] = useState('');
  const [vehiculo, setVehiculo] = useState('');
  const [dedicacion, setDedicacion] = useState('');
  const [fuente, setFuente] = useState('');
  const [valoracionMin, setValoracionMin] = useState('');
  const [carnet, setCarnet] = useState('');
  const [localidad, setLocalidad] = useState('');
  const [experienciaMin, setExperienciaMin] = useState('');

  const [nuevoManual, setNuevoManual] = useState(false);
  const [nuevoDesdeCv, setNuevoDesdeCv] = useState(false);
  const [menuNuevo, setMenuNuevo] = useState(false);
  const [seleccion, setSeleccion] = useState<string[]>([]);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const [enBloque, setEnBloque] = useState(false);

  const consulta = useMemo(() => {
    const q: Record<string, string> = {};
    if (pestana === 'caducados') q.caducados = '1';
    else if (pestana !== 'all') q.estado = pestana;
    if (puesto) q.puesto = puesto;
    if (zona) q.zona = zona;
    if (vehiculo) q.vehiculo = vehiculo;
    if (dedicacion) q.dedicacion = dedicacion;
    if (fuente) q.fuente = fuente;
    if (valoracionMin) q.valoracionMin = valoracionMin;
    if (carnet) q.carnet = carnet;
    if (localidad) q.localidad = localidad;
    if (experienciaMin) q.experienciaMin = experienciaMin;
    if (busqueda.trim()) {
      q.q = busqueda.trim();
      if (enCv) q.enCv = '1';
    }
    return q;
  }, [
    pestana, puesto, zona, vehiculo, dedicacion, fuente, valoracionMin,
    carnet, localidad, experienciaMin, busqueda, enCv,
  ]);

  const { data, loading, error, refresh } = useApi<Candidato[]>(
    () => api.getCandidatos(consulta),
    [JSON.stringify(consulta)],
  );

  const { data: contadores, refresh: refrescarContadores } = useApi<ContadoresCandidatos>(
    () => api.getContadoresCandidatos(),
    [],
  );

  const candidatos = useMemo(() => data ?? [], [data]);

  const recargar = () => {
    refresh();
    refrescarContadores();
    setSeleccion([]);
  };

  const filtrosActivos = [
    puesto, zona, vehiculo, carnet, localidad, dedicacion, fuente, valoracionMin, experienciaMin,
  ].filter(Boolean).length;

  const limpiarFiltros = () => {
    setPuesto('');
    setZona('');
    setVehiculo('');
    setCarnet('');
    setLocalidad('');
    setDedicacion('');
    setFuente('');
    setValoracionMin('');
    setExperienciaMin('');
  };

  const cambiarPestana = (key: Pestana) => {
    const nuevos = new URLSearchParams(params);
    if (key === 'nuevo') nuevos.delete('estado');
    else nuevos.set('estado', key);
    setParams(nuevos, { replace: true });
    setSeleccion([]);
  };

  const crearManual = async (datos: Record<string, unknown>, forzar: boolean) => {
    await api.crearCandidato(datos, forzar);
    setNuevoManual(false);
    recargar();
  };

  /**
   * Abre la ficha al pulsar en cualquier parte de la fila o de la tarjeta.
   *
   * Salvo que el clic haya caído sobre algo que ya hace lo suyo: la casilla de
   * los caducados, el enlace al currículum o el propio nombre. Sin esta guarda,
   * marcar una casilla te sacaría de la lista.
   *
   * El nombre sigue siendo un enlace de verdad, así que el teclado, el botón
   * central y «abrir en otra pestaña» siguen funcionando; esto solo añade la
   * comodidad de no tener que apuntar.
   */
  const abrirFicha = (e: React.MouseEvent, id: string) => {
    if ((e.target as HTMLElement).closest('a, button, input, label, select')) return;
    navegar(`/equipo/candidatos/${id}`);
  };

  const alternarSeleccion = (id: string) =>
    setSeleccion((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const eliminarSeleccion = async () => {
    setEnBloque(true);
    try {
      await api.eliminarCandidatosEnLote(seleccion);
      setConfirmarBorrado(false);
      recargar();
    } finally {
      setEnBloque(false);
    }
  };

  return (
    <div>
      {/* Cabecera */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
        {/* En el móvil solo el título: la frase de debajo ocupaba cinco líneas
            y un tercio de la pantalla antes de que se viera un candidato. */}
        <div className="flex items-center gap-2 min-w-0">
          <UserSearch size={22} className="text-sereno-dark shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">Candidatos</h1>
            <p className="hidden sm:block text-sm text-sereno-dark/40 mt-0.5">
              Bolsa de empleo: quién nos ha mandado el currículum y para qué.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href={api.exportCandidatosUrl(
              pestana !== 'all' && pestana !== 'caducados' ? { estado: pestana } : undefined,
            )}
            className="btn-ghost h-11 flex items-center gap-2 text-sm"
            title="Exportar a Excel"
          >
            <Download size={16} />
            <span className="hidden lg:inline">Exportar</span>
          </a>
          <button
            onClick={recargar}
            disabled={loading}
            className="btn-ghost h-11 flex items-center gap-2 text-sm"
            title="Recargar la lista"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          </button>

          <div className="relative">
            <button
              onClick={() => setMenuNuevo((v) => !v)}
              className="btn-primary h-11 flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Nuevo</span>
            </button>
            {menuNuevo && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuNuevo(false)} />
                <div className="absolute right-0 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg z-20 overflow-hidden">
                  <button
                    onClick={() => {
                      setMenuNuevo(false);
                      setNuevoDesdeCv(true);
                    }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-sereno-blue/10 flex items-center gap-2"
                  >
                    <Sparkles size={15} className="text-sereno-dark/50" />
                    Desde su CV
                  </button>
                  <button
                    onClick={() => {
                      setMenuNuevo(false);
                      setNuevoManual(true);
                    }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-sereno-blue/10 border-t border-gray-100"
                  >
                    Rellenar a mano
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Pestañas por estado */}
      <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-none mb-4">
        <div className="flex gap-2 min-w-max">
          {PESTANAS.map((p) => {
            const activa = pestana === p.key;
            const n = contadores?.[p.key as keyof ContadoresCandidatos] ?? 0;
            if (p.key === 'caducados' && !n && !activa) return null;
            return (
              <button
                key={p.key}
                onClick={() => cambiarPestana(p.key)}
                aria-pressed={activa}
                className={classNames(
                  'h-11 px-4 rounded-xl border-2 text-sm font-medium transition-colors whitespace-nowrap',
                  activa
                    ? 'border-sereno-dark bg-sereno-dark text-white'
                    : p.key === 'caducados'
                      ? 'border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300'
                      : 'border-gray-100 bg-white hover:border-slate-300',
                )}
              >
                {p.label}
                <span className={classNames('ml-1.5', activa ? 'opacity-70' : 'opacity-50')}>{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Buscador y filtros */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Nombre, teléfono, email, localidad…"
            className="input pl-9 h-11 w-full"
          />
        </div>
        <button
          onClick={() => setFiltrosAbiertos((v) => !v)}
          className={classNames(
            'h-11 px-4 rounded-xl border-2 text-sm font-medium flex items-center gap-2 transition-colors',
            filtrosActivos
              ? 'border-sereno-dark bg-sereno-dark text-white'
              : 'border-gray-100 bg-white hover:border-slate-300',
          )}
        >
          <Filter size={16} />
          <span className="hidden sm:inline">Filtros</span>
          {filtrosActivos > 0 && <span className="opacity-70">{filtrosActivos}</span>}
        </button>
      </div>

      {busqueda.trim() && (
        <label className="flex items-center gap-2 text-sm text-sereno-dark/60 mb-3 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={enCv}
            onChange={(e) => setEnCv(e.target.checked)}
          />
          Buscar también dentro del texto de los currículums
        </label>
      )}

      {filtrosAbiertos && (
        <div className="card mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="label">Puesto</label>
            <select className="input h-11" value={puesto} onChange={(e) => setPuesto(e.target.value)}>
              <option value="">Cualquiera</option>
              {catalogos.puestos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Zona</label>
            <select className="input h-11" value={zona} onChange={(e) => setZona(e.target.value)}>
              <option value="">Cualquiera</option>
              {catalogos.zonas.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Vehículo</label>
            <select className="input h-11" value={vehiculo} onChange={(e) => setVehiculo(e.target.value)}>
              <option value="">Indiferente</option>
              <option value="si">Con vehículo</option>
              <option value="no">Sin vehículo</option>
              <option value="desconocido">No consta</option>
            </select>
          </div>
          <div>
            <label className="label">Carnet de conducir</label>
            <select className="input h-11" value={carnet} onChange={(e) => setCarnet(e.target.value)}>
              <option value="">Indiferente</option>
              <option value="si">Con carnet</option>
              <option value="no">Sin carnet</option>
              <option value="desconocido">No consta</option>
            </select>
          </div>
          <div>
            <label className="label">Localidad</label>
            <select
              className="input h-11"
              value={localidad}
              onChange={(e) => setLocalidad(e.target.value)}
            >
              <option value="">Cualquiera</option>
              {(contadores?.localidades || []).map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Experiencia mínima</label>
            <select
              className="input h-11"
              value={experienciaMin}
              onChange={(e) => setExperienciaMin(e.target.value)}
            >
              <option value="">Cualquiera</option>
              {[1, 2, 3, 5, 10].map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? 'año' : 'años'} o más
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Dedicación</label>
            <select
              className="input h-11"
              value={dedicacion}
              onChange={(e) => setDedicacion(e.target.value)}
            >
              <option value="">Cualquiera</option>
              {Object.entries(DEDICACION_LABELS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Origen</label>
            <select className="input h-11" value={fuente} onChange={(e) => setFuente(e.target.value)}>
              <option value="">Cualquiera</option>
              {Object.entries(FUENTE_LABELS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Valoración mínima</label>
            <select
              className="input h-11"
              value={valoracionMin}
              onChange={(e) => setValoracionMin(e.target.value)}
            >
              <option value="">Cualquiera</option>
              {[5, 4, 3, 2].map((n) => (
                <option key={n} value={n}>
                  {n} estrellas o más
                </option>
              ))}
            </select>
          </div>
          {filtrosActivos > 0 && (
            <div className="sm:col-span-2 lg:col-span-3">
              <button onClick={limpiarFiltros} className="btn-ghost h-10 text-sm flex items-center gap-1.5">
                <X size={14} /> Quitar los filtros
              </button>
            </div>
          )}
        </div>
      )}

      {/* Acciones sobre los caducados */}
      {pestana === 'caducados' && candidatos.length > 0 && (
        <div className="card bg-amber-50 border-amber-200 mb-4 py-3">
          <p className="text-sm text-amber-900 flex items-start gap-2 mb-3">
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            <span>
              Estos currículums han cumplido el plazo de conservación: bórralos. Si alguien te ha
              pedido seguir en la bolsa, renuévalo desde su ficha.
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() =>
                setSeleccion(seleccion.length === candidatos.length ? [] : candidatos.map((c) => c.id))
              }
              className="btn-ghost h-10 text-sm"
            >
              {seleccion.length === candidatos.length ? 'Ninguno' : 'Seleccionar todos'}
            </button>
            <button
              onClick={() => setConfirmarBorrado(true)}
              disabled={!seleccion.length || enBloque}
              className="h-10 px-4 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <Trash2 size={14} /> Eliminar ({seleccion.length})
            </button>
          </div>
        </div>
      )}

      {loading && !data && <LoadingSpinner />}

      {error && (
        <div className="card bg-red-50 border-red-200 text-red-700 text-sm">
          No se ha podido cargar la lista: {error}
        </div>
      )}

      {!loading && !error && candidatos.length === 0 && (
        <EmptyState
          title={busqueda || filtrosActivos ? 'Nada por aquí' : 'Todavía no hay candidatos'}
          description={
            busqueda || filtrosActivos
              ? 'Prueba con otro filtro o cambia la búsqueda.'
              : 'Sube el primer currículum con el botón «Nuevo».'
          }
        />
      )}

      {candidatos.length > 0 && (
        <>
          {/* Tarjetas (móvil) */}
          <div className="space-y-3 md:hidden">
            {candidatos.map((c) => (
              <Tarjeta
                key={c.id}
                candidato={c}
                labels={catalogos.puestoLabel}
                seleccionable={pestana === 'caducados'}
                seleccionado={seleccion.includes(c.id)}
                onSeleccionar={() => alternarSeleccion(c.id)}
                onAbrir={(e) => abrirFicha(e, c.id)}
              />
            ))}
          </div>

          {/* Tabla (escritorio) */}
          <div className="card overflow-x-auto hidden md:block p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-sereno-dark/50">
                  {pestana === 'caducados' && <th className="px-3 py-3 w-10" />}
                  <th className="px-4 py-3 font-medium">Candidato</th>
                  <th className="px-4 py-3 font-medium">Puestos</th>
                  <th className="px-4 py-3 font-medium min-w-[16rem]">Experiencia</th>
                  <th className="px-4 py-3 font-medium">Zonas</th>
                  <th className="px-4 py-3 font-medium">Disponible</th>
                  <th className="px-4 py-3 font-medium">Valoración</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Alta</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {candidatos.map((c) => {
                  const cv = cvPrincipal(c);
                  const idiomas = idiomasUtiles(c);
                  return (
                    <tr
                      key={c.id}
                      onClick={(e) => abrirFicha(e, c.id)}
                      className="border-b border-gray-50 last:border-0 hover:bg-slate-50 cursor-pointer"
                    >
                      {pestana === 'caducados' && (
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={seleccion.includes(c.id)}
                            onChange={() => alternarSeleccion(c.id)}
                            aria-label={`Seleccionar a ${nombreCompleto(c)}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <Link
                          to={`/equipo/candidatos/${c.id}`}
                          className="font-medium hover:underline flex items-center gap-1.5"
                        >
                          {nombreCompleto(c)}
                          {c.vehiculo === 'si' && (
                            <Car size={14} className="text-sereno-dark/40" aria-label="Con vehículo" />
                          )}
                          {c.carnet === 'si' && c.vehiculo !== 'si' && (
                            <IdCard size={14} className="text-sereno-dark/40" aria-label="Con carnet" />
                          )}
                          {idiomas.length > 0 && (
                            <Languages
                              size={14}
                              className="text-sereno-dark/40"
                              aria-label={`Idiomas: ${idiomas.join(', ')}`}
                            />
                          )}
                        </Link>
                        <div className="text-xs text-sereno-dark/40 mt-0.5">
                          {c.telefono || c.email || 'Sin contacto'}
                          {c.localidad && ` · ${c.localidad}`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <ChipsCatalogo ids={c.puestos} labels={catalogos.puestoLabel} />
                      </td>
                      <td className="px-4 py-3 text-sereno-dark/60 align-top">
                        {c.estadoIa === 'leyendo' ? (
                          <span className="flex items-center gap-1.5 text-sereno-dark/40">
                            <Loader2 size={13} className="animate-spin" /> leyendo el CV…
                          </span>
                        ) : (
                          <>
                            {c.experienciaAnios !== undefined && (
                              <span className="font-medium text-sereno-dark/80">
                                {c.experienciaAnios} {c.experienciaAnios === 1 ? 'año' : 'años'}
                              </span>
                            )}
                            <p className="text-xs leading-snug line-clamp-2">
                              {experienciaBreve(c) || '—'}
                            </p>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sereno-dark/60">
                        {c.zonas?.length
                          ? c.zonas.map((z) => catalogos.zonaLabel[z] || z).join(', ')
                          : 'Cualquiera'}
                      </td>
                      <td className="px-4 py-3 text-sereno-dark/60">
                        {DEDICACION_LABELS[c.disponibilidad?.dedicacion] || '—'}
                        {c.disponibilidad?.desde && (
                          <div className="text-xs text-sereno-dark/40">
                            desde {formatDate(c.disponibilidad.desde)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Estrellas valor={c.valoracion} tamano={14} />
                      </td>
                      <td className="px-4 py-3">
                        <EstadoBadge estado={c.estado} />
                      </td>
                      <td className="px-4 py-3 text-sereno-dark/50">{formatDate(c.createdAt)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {cv && (
                          <a
                            href={api.urlDocumentoCandidato(c.id, cv.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-lg hover:bg-slate-100"
                            title="Abrir el currículum"
                          >
                            <FileUser size={16} />
                          </a>
                        )}
                        {/* Ya no es un enlace: la fila entera abre la ficha, y el
                            nombre sigue siéndolo para el teclado y para abrir en
                            otra pestaña. Se queda como señal de que la fila se
                            puede pulsar. */}
                        <span
                          aria-hidden="true"
                          className="inline-flex items-center justify-center h-9 w-9 text-sereno-dark/30"
                        >
                          <ChevronRight size={16} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Alta */}
      <Modal
        isOpen={nuevoManual}
        onClose={() => setNuevoManual(false)}
        title="Nuevo candidato"
        size="lg"
      >
        <FormularioCandidato onGuardar={crearManual} onCancelar={() => setNuevoManual(false)} />
      </Modal>

      {nuevoDesdeCv && (
        <NuevoDesdeCv
          onCerrar={() => setNuevoDesdeCv(false)}
          onCreado={(c) => {
            setNuevoDesdeCv(false);
            // A la ficha, donde se ve cómo va la lectura del CV, que ya está
            // en marcha desde que el servidor guardó el fichero.
            navegar(`/equipo/candidatos/${c.id}`);
          }}
        />
      )}

      <Modal
        isOpen={confirmarBorrado}
        onClose={() => !enBloque && setConfirmarBorrado(false)}
        title="Eliminar definitivamente"
      >
        <div className="space-y-4">
          <p className="text-sm">
            Se borrarán {seleccion.length}{' '}
            {seleccion.length === 1 ? 'candidato' : 'candidatos'} con sus currículums y su
            historial. No se puede deshacer.
          </p>
          <ul className="text-sm text-sereno-dark/60 list-disc pl-5 max-h-40 overflow-y-auto">
            {candidatos
              .filter((c) => seleccion.includes(c.id))
              .map((c) => (
                <li key={c.id}>{nombreCompleto(c)}</li>
              ))}
          </ul>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setConfirmarBorrado(false)}
              disabled={enBloque}
              className="btn-ghost h-11 text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={() => void eliminarSeleccion()}
              disabled={enBloque}
              className="h-11 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium flex items-center gap-2"
            >
              {enBloque && <Loader2 size={16} className="animate-spin" />}
              Eliminar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Tarjeta de móvil ─────────────────────────────────────────────────────

function Tarjeta({
  candidato: c,
  labels,
  seleccionable,
  seleccionado,
  onSeleccionar,
  onAbrir,
}: {
  candidato: Candidato;
  labels: Record<string, string>;
  seleccionable: boolean;
  seleccionado: boolean;
  onSeleccionar: () => void;
  onAbrir: (e: React.MouseEvent) => void;
}) {
  const idiomas = idiomasUtiles(c);

  return (
    // La tarjeta entera abre la ficha, no solo el trozo que ocupa el enlace:
    // en un teléfono, el margen de la tarjeta es superficie que se pulsa.
    <div onClick={onAbrir} className="card active:bg-slate-50 transition-colors cursor-pointer">
      <div className="flex items-start gap-3">
        {seleccionable && (
          <input
            type="checkbox"
            className="h-5 w-5 mt-1 flex-shrink-0"
            checked={seleccionado}
            onChange={onSeleccionar}
            aria-label={`Seleccionar a ${nombreCompleto(c)}`}
          />
        )}

        <Link to={`/equipo/candidatos/${c.id}`} className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium truncate">{nombreCompleto(c)}</p>
              <p className="text-sm text-sereno-dark/50 mt-0.5">
                {c.telefono || c.email || 'Sin contacto'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <EstadoBadge estado={c.estado} />
              <Estrellas valor={c.valoracion} tamano={13} />
            </div>
          </div>

          {c.puestos?.length > 0 && (
            <div className="mt-2">
              <ChipsCatalogo ids={c.puestos} labels={labels} />
            </div>
          )}

          {c.estadoIa === 'leyendo' ? (
            <p className="text-xs text-sereno-dark/40 mt-2 flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> leyendo el CV…
            </p>
          ) : (
            experienciaBreve(c) && (
              <p className="text-sm text-sereno-dark/60 mt-2 line-clamp-2">
                {c.experienciaAnios !== undefined && (
                  <span className="font-medium">
                    {c.experienciaAnios} {c.experienciaAnios === 1 ? 'año' : 'años'} ·{' '}
                  </span>
                )}
                {experienciaBreve(c)}
              </p>
            )
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-sereno-dark/50">
            {c.localidad && (
              <span className="flex items-center gap-1">
                <MapPin size={12} /> {c.localidad}
              </span>
            )}
            {c.vehiculo === 'si' && (
              <span className="flex items-center gap-1">
                <Car size={12} /> coche
              </span>
            )}
            {c.carnet === 'si' && (
              <span className="flex items-center gap-1">
                <IdCard size={12} /> carnet
              </span>
            )}
            {idiomas.length > 0 && (
              <span className="flex items-center gap-1">
                <Languages size={12} /> {idiomas.join(', ')}
              </span>
            )}
            {c.documentos?.length > 0 && (
              <span className="flex items-center gap-1">
                <FileUser size={12} /> {c.documentos.length}
              </span>
            )}
          </div>
        </Link>
      </div>
    </div>
  );
}
